import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RemoveIcon from "@mui/icons-material/Remove";
import { alpha, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import { useUnreadMessagesContext } from "../../context/unread-messages/UnreadMessagesContext";
import { CompanyAvatar } from "../common/CompanyAvatar";
import { Contact, getContactId, useInternalContacts } from "../internal-contacts";
import { JobChatView } from "../job-chats";
import { GroupChatWrapper } from "../message-groups";
import { useMessageSearch } from "../Messages/hooks/useMessageSearch";
import { MessagesView } from "../Messages/view/MessageView";
import { useBulkResolveContacts } from "../virtual-receptionist/contacts/hooks/useBulkResolveContacts";
import { useContactDetailsModal } from "../virtual-receptionist/contacts/hooks/useContactDetailsModal";
import { useVirtualAssistant } from "../virtual-receptionist/context/VirtualAssistantContext";
import { SmsChatView } from "../virtual-receptionist/sms/chat/SmsChatView";
import { useContactCenterTheme } from "../virtual-receptionist/theming/ContactCenterThemeContext";
import { ContactCenterThemeScope } from "../virtual-receptionist/theming/ContactCenterThemeScope";
import { OpenChat } from "./chatDock.types";
import { useChatDock } from "./ChatDockContext";
import { buildChatDockLabel, resolveSmsChatName } from "./helpers/resolveChatLabel";
import { InternalChatHeader } from "./InternalChatHeader";
import { DOCK_HEADER_HEIGHT } from "./useChatDockLayout";

interface Props {
  chat: OpenChat;
  width: number;
  height: number;
  isFocused: boolean;
}

export function ChatDockWindow({ chat, width, height, isFocused }: Props) {
  const { theme } = useContactCenterTheme();
  const { closeChat, toggleMinimize, focusChat, clearMessageFocus } = useChatDock();
  const { sessionNumber } = useVirtualAssistant();
  const { openContactDetails, openAddContactMenu } = useContactDetailsModal();
  const { setActiveContact } = useUnreadMessagesContext();
  const activeCompanyId = useSelector((state: any) => state.userState.activeCompanyId);
  const userCompanies = useSelector((state: any) => state.userState.companies);
  const currentCompany = useSelector((state: any) => state.userState.company);

  const smsTarget = chat.target.type === "sms" ? chat.target : null;

  const { data: resolvedByPhone = {} } = useBulkResolveContacts(
    smsTarget && !smsTarget.contact?.contact ? [smsTarget.phoneNumber] : [],
  );

  const chatName = useMemo(() => {
    if (!smsTarget) return chat.chatName;

    const savedContact = smsTarget.contact?.contact ?? resolvedByPhone[smsTarget.phoneNumber] ?? null;

    return resolveSmsChatName(smsTarget.phoneNumber, savedContact);
  }, [smsTarget, resolvedByPhone, chat.chatName]);

  const internalTarget = chat.target.type === "internal" ? chat.target : null;

  const dockCompany = useMemo(() => {
    const dockCompanyId = (chat.target as { companyId?: number | string | null }).companyId ?? activeCompanyId;
    const membership = (userCompanies ?? []).find(
      (item: any) => String(item?.companyId ?? item?.company?.id ?? item?.id) === String(dockCompanyId),
    );

    return membership?.company ?? membership ?? currentCompany;
  }, [chat.target, activeCompanyId, userCompanies, currentCompany]);

  const dockCompanyName = dockCompany?.businessName || dockCompany?.name || chat.companyName || "";

  const label = internalTarget ? dockCompanyName || chatName : buildChatDockLabel(chat.companyName, chatName);

  const internalSearch = useMessageSearch();

  const { contacts: internalContacts } = useInternalContacts();

  const internalContact = useMemo<Contact | undefined>(() => {
    if (!internalTarget) return undefined;

    return (
      (internalTarget.contact as Contact | undefined) ??
      internalContacts.find((contact) => String(getContactId(contact)) === String(internalTarget.contactId))
    );
  }, [internalTarget, internalContacts]);

  const setActiveContactRef = useRef(setActiveContact);
  setActiveContactRef.current = setActiveContact;

  const internalContactRef = useRef(internalContact);
  internalContactRef.current = internalContact;

  const internalContactId = internalTarget?.contactId ?? null;
  const isChatActive = isFocused && !chat.minimized;
  const shouldClaimActiveContact = Boolean(internalContact) && isChatActive;
  const focusMessageUid = chat.focusMessageUid ? String(chat.focusMessageUid) : null;
  const focusMessageText = chat.focusMessageText ? String(chat.focusMessageText) : null;
  const focusMessageRequestKey = focusMessageUid || focusMessageText ? `${focusMessageUid ?? focusMessageText}:${chat.lastActivityAt}` : null;
  const handleFocusMessageConsumed = useCallback(() => {
    clearMessageFocus(chat.key);
  }, [chat.key, clearMessageFocus]);

  useEffect(() => {
    if (!shouldClaimActiveContact) return;

    const contact = internalContactRef.current;
    if (!contact) return;

    setActiveContactRef.current(contact);

    return () => setActiveContactRef.current(null);
  }, [shouldClaimActiveContact, internalContactId]);

  const handleHeaderClick = useCallback(() => {
    if (chat.minimized) {
      focusChat(chat.key);
      return;
    }

    if (!isFocused) focusChat(chat.key);
  }, [chat.minimized, chat.key, focusChat, isFocused]);

  const body = useMemo(() => {
    if (chat.target.type === "sms") {
      return (
        <SmsChatView
          hideBack
          isActive={isChatActive}
          onBack={() => closeChat(chat.key)}
          sessionNumber={chat.target.sessionNumber ?? sessionNumber}
          companyId={chat.target.companyId}
          focusMessageUid={focusMessageUid}
          focusMessageText={focusMessageText}
          focusMessageRequestKey={focusMessageRequestKey}
          onFocusMessageConsumed={handleFocusMessageConsumed}
          contact={chat.target.contact ?? { phoneNumber: chat.target.phoneNumber }}
          onQuickSave={openAddContactMenu}
          onEditContact={(contact) => openContactDetails(contact.phoneNumber, contact.contact)}
        />
      );
    }

    if (chat.target.type === "internal") {
      if (!internalContact) return null;

      return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
          <InternalChatHeader contact={internalContact} search={internalSearch} />

          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <MessagesView
              selectedContact={internalContact}
              focusMessageUid={focusMessageUid}
              focusMessageText={focusMessageText}
              focusMessageRequestKey={focusMessageRequestKey}
              onFocusMessageConsumed={handleFocusMessageConsumed}
              search={internalSearch}
            />
          </Box>
        </Box>
      );
    }

    if (chat.target.type === "group") {
      return (
        <GroupChatWrapper
          groupUid={chat.target.groupUid}
          companyId={chat.target.companyId ?? activeCompanyId}
          fallbackName={chat.chatName}
          isFocused={isChatActive}
          focusMessageUid={focusMessageUid}
          focusMessageText={focusMessageText}
          focusMessageRequestKey={focusMessageRequestKey}
          onFocusMessageConsumed={handleFocusMessageConsumed}
          onClosed={() => closeChat(chat.key)}
        />
      );
    }

    return (
      <JobChatView
        hideHeader
        isFocused={isChatActive}
        jobEventUid={chat.target.jobEventUid}
        companyId={chat.target.companyId ?? activeCompanyId}
        status={chat.target.chat?.status}
        jobAlias={chat.target.chat?.alias}
        focusMessageUid={focusMessageUid}
        focusMessageText={focusMessageText}
        focusMessageRequestKey={focusMessageRequestKey}
        onFocusMessageConsumed={handleFocusMessageConsumed}
      />
    );
  }, [
    chat.key,
    chat.target,
    isChatActive,
    sessionNumber,
    internalContact,
    internalSearch,
    activeCompanyId,
    closeChat,
    openAddContactMenu,
    openContactDetails,
    focusMessageUid,
    focusMessageText,
    focusMessageRequestKey,
    handleFocusMessageConsumed,
  ]);

  return (
    <ContactCenterThemeScope>
      <Box
        onMouseDown={handleHeaderClick}
        sx={{
          width,
          minWidth: width,
          maxWidth: width,
          height: chat.minimized ? DOCK_HEADER_HEIGHT : height,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: "12px 12px 0 0",
          backgroundColor: theme.shell.bg,
          color: theme.shell.textPrimary,
          border: "1px solid",
          borderColor: isFocused ? alpha(theme.shell.accent, 0.55) : theme.shell.border,
          borderBottom: "none",
          boxShadow: isFocused ? "0 -6px 24px rgba(0,0,0,0.16)" : "0 -2px 12px rgba(0,0,0,0.1)",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        }}
      >
        <Box
          onClick={handleHeaderClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.25,
            height: DOCK_HEADER_HEIGHT,
            minHeight: DOCK_HEADER_HEIGHT,
            flexShrink: 0,
            cursor: "pointer",
            backgroundColor: theme.shell.headerBg,
            borderBottom: chat.minimized ? "none" : `1px solid ${theme.shell.border}`,
          }}
        >
          {chat.unreadCount > 0 && (
            <Box
              sx={{
                minWidth: 18,
                height: 18,
                px: chat.unreadCount > 9 ? 0.5 : 0,
                borderRadius: "999px",
                backgroundColor: theme.shell.accent,
                color: "#fff",
                fontSize: 10,
                fontWeight: 800,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              {chat.unreadCount > 99 ? "99+" : chat.unreadCount}
            </Box>
          )}

          {internalTarget && (
            <Box sx={{ flexShrink: 0, display: "grid", placeItems: "center" }}>
              <CompanyAvatar company={dockCompany} size={22} />
            </Box>
          )}

          <Tooltip title={label} enterDelay={500} slotProps={{ popper: { sx: { zIndex: "2147483647 !important" } } }}>
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: 13.5,
                fontWeight: 700,
                color: theme.shell.textPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              {label}
            </Typography>
          </Tooltip>

          <IconButton
            size="small"
            aria-label={chat.minimized ? "Expand chat" : "Minimize chat"}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              toggleMinimize(chat.key);
            }}
            sx={{ width: 28, height: 28, color: theme.shell.textSecondary }}
          >
            {chat.minimized ? <KeyboardArrowUpIcon sx={{ fontSize: 20 }} /> : <RemoveIcon sx={{ fontSize: 18 }} />}
          </IconButton>

          <IconButton
            size="small"
            aria-label="Close chat"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              closeChat(chat.key);
            }}
            sx={{ width: 28, height: 28, color: theme.shell.textSecondary }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box
          aria-hidden={chat.minimized}
          sx={{
            height: Math.max(height - DOCK_HEADER_HEIGHT, 0),
            minHeight: Math.max(height - DOCK_HEADER_HEIGHT, 0),
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {body}
        </Box>
      </Box>
    </ContactCenterThemeScope>
  );
}
